import React, { useEffect, useState } from 'react';
import { Box, Typography, Checkbox, Select, MenuItem, IconButton } from '@mui/material';
import TextInput from '../../../inputs/textInput';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface FormLoadingProps {
  data?: {
    output?: {
      loading_country?: string[];
      loading_country_post_code?: string[];
      total_weight?: string;
      total_volume?: string;
      loading_reference_number?: string;
      loading_country_address?: string;
      loading_place_from_date?: string[];
      loading_place_to_date?: string[];
      loading_city?: string;
      temperature?: string;
      loading_company?: string;
    };
  };
}

type FormType = {
  id?: number;
  loading?: string;
  fromDate?: string;
  toDate?: string;
  sender?: string;
  search?: string;
};

const FormLoading = ({ data }: FormLoadingProps) => {
  const [forms, setForms] = useState<FormType[]>([{}]);

  const addForm = () => {
    const newForm = { id: Date.now() };
    setForms([...forms, newForm]);
  };
  const removeForm = (index: number) => {
    if (forms.length > 1) {
      const updatedForms = forms.filter((form, _i) => form.id !== forms[index].id);
      setForms(updatedForms);
    }
  };

  const [formData, setFormData] = useState({
    loading_country: data?.output?.loading_country?.[0] || '',
    loading_country_post_code: data?.output?.loading_country_post_code?.[0] || '',
    total_weight: data?.output?.total_weight || '',
    total_volume: data?.output?.total_volume || '',
    loading_reference_number: data?.output?.loading_reference_number || '',
    loading_country_address: data?.output?.loading_country_address || '',
    loading_place_from_date: data?.output?.loading_place_from_date || '',
    loading_place_to_date: data?.output?.loading_place_to_date || '',
    loading_city: data?.output?.loading_city || '',
    temperature: data?.output?.temperature || '',
    loading_company: data?.output?.loading_company || '',
  });

  useEffect(() => {
    const formatDate = (dateStr: string) => typeof dateStr === 'string' ? dateStr.slice(0, 16) : '';

    const rawFrom = data?.output?.loading_place_from_date?.[0] || '';
    const rawTo = data?.output?.loading_place_to_date?.[0] || '';
    setFormData({
      loading_country: data?.output?.loading_country?.[0] || '',
      loading_country_post_code: data?.output?.loading_country_post_code?.[0] || '',
      total_weight: data?.output?.total_weight || '',
      total_volume: data?.output?.total_volume || '',
      loading_reference_number: data?.output?.loading_reference_number || '',
      loading_country_address: data?.output?.loading_country_address || '',
      loading_place_from_date: formatDate(rawFrom),
      loading_place_to_date: formatDate(rawTo),
      loading_city: data?.output?.loading_city || '',
      temperature: data?.output?.temperature || '',
      loading_company: data?.output?.loading_company || '',
    });
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value as string }));
  };

  const inputSx = {
    borderRadius: 1,
    px: 0.25,
    py: 0.125,
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid border.medium',
    },
  };

  const borderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
  };

  const redBorderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
  };

  const greenBorderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.blue',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        p: 2.5,
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 1,
        mb: 1,
      }}
    >
      <Box component="ul" id="loading-forms-list" sx={{ width: '100%', flex: 1, listStyle: 'none', p: 0, m: 0 }}>
        {forms.map((form, index) => (
          <Box component="li" key={form.id} sx={{ display: 'flex' }}>
            <Box component="form" sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{
                        color: 'text.blue',
                        fontWeight: 700,
                        fontSize: (theme: any) => theme.typography.fontSizes.body,
                        width: '120px',
                        display: 'inline-block',
                        textAlign: 'right',
                        mr: 1,
                      }}
                    >
                      PAKROVIMAS
                    </Typography>
                    <TextInput
                      type="text"
                      id="loading_company"
                      name="loading_company"
                      value={formData.loading_company}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, mr: 5.25 }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 3.625, width: '180px' }}
                    />
                    <TextInput
                      type="text"
                      id="loading-address"
                      name="loading-address"
                      value={formData.loading_country_address}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, mr: 1, ml: 3.5, width: '315px' }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, width: '45px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ ml: 5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      FIX
                    </Typography>
                    <Checkbox />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      NUO
                    </Typography>
                    <TextInput
                      type="datetime-local"
                      id="loading_place_from_date"
                      name="loading_place_from_date"
                      value={formData.loading_place_from_date}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      IKI
                    </Typography>
                    <TextInput
                      type="datetime-local"
                      id="from-date"
                      name="from-date"
                      value={formData.loading_place_to_date}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, ml: 4.75, width: '180px' }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 4.125, width: '365px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      NR.
                    </Typography>
                    <TextInput
                      type="number"
                      id="loading-start-date"
                      size="small"
                      sx={{ ...borderInputSx, width: '30px' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        component="label"
                        sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1, ml: -1.125 }}
                      >
                        EIL. NR.
                      </Typography>
                      <TextInput
                        type="number"
                        id="loading-start-date2"
                        size="small"
                        sx={{ ...borderInputSx, width: '30px' }}
                      />
                    </Box>
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small }}
                    >
                      TEL.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 4.375, width: '180px' }}
                    />
                    <TextInput
                      type="text"
                      id="loading_country"
                      name="loading_country"
                      value={formData.loading_country}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...redBorderInputSx, mr: 0.625, ml: 3.5, width: '45px' }}
                    />
                    <TextInput
                      type="text"
                      id="loading_country_post_code"
                      name="loading_country_post_code"
                      value={formData.loading_country_post_code}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...greenBorderInputSx, mr: 0.625, ml: 3.5, width: '65px' }}
                    />
                    <TextInput
                      type="text"
                      id="loading_city"
                      name="loading_city"
                      value={formData.loading_city}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, ml: 0.125 }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      SIUNTĖJAS
                    </Typography>
                    <TextInput
                      type="text"
                      size="small"
                      sx={{ ...borderInputSx, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      CMR NR.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 3.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      PLAT.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '95px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      ILG.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '95px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      PAIEŠKA PLAT., ILG.
                    </Typography>
                    <TextInput
                      type="text"
                      size="small"
                      sx={{ ...borderInputSx, width: '180px' }}
                    />
                    <TextInput
                      name="loading_reference_number"
                      type="text"
                      value={formData.loading_reference_number}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...borderInputSx, width: '365px', ml: 35.5 }}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, color: 'text.blue', ml: 1.25, width: '100%' }}>
                  <Box>
                    <TextInput
                      type="text"
                      id="total_weight"
                      name="total_weight"
                      value={formData.total_weight}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...borderInputSx, width: '77px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      SVORIS KG.
                    </Typography>
                    <TextInput
                      type="text"
                      id="total_volume"
                      name="total_volume"
                      value={formData.total_volume}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      TŪRIS.
                    </Typography>
                    <TextInput
                      type="number"
                      id="width"
                      name="width"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      PLOTIS M.
                    </Typography>
                    <Select
                      defaultValue="AKTYVUS"
                      size="small"
                      sx={{ ...borderInputSx, width: '97px', fontSize: (theme: any) => theme.typography.fontSizes.small, bgcolor: 'background.white', ml: 1 }}
                    >
                      <MenuItem value="AKTYVUS">AKTYVUS</MenuItem>
                      <MenuItem value="NEAKTYVUS">NEAKTYVUS</MenuItem>
                    </Select>
                  </Box>

                  <Box>
                    <TextInput
                      id="height"
                      name="height"
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      AUKŠTIS M.
                    </Typography>
                    <TextInput
                      type="number"
                      id="ldm"
                      name="ldm"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      LDM
                    </Typography>
                    <TextInput
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      TAX SVORIS
                    </Typography>
                  </Box>

                  <Box>
                    <TextInput
                      type="text"
                      id="temperature"
                      name="temperature"
                      value={formData.temperature}
                      size="small"
                      sx={{ ...borderInputSx, width: '77px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      TEMP °C
                    </Typography>
                    <TextInput
                      type="number"
                      id="quantity"
                      name="quantity"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      KIEKIS.
                    </Typography>
                    <TextInput
                      id="length"
                      name="length"
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      ILGIS M.
                    </Typography>
                  </Box>

                  <Box>
                    <TextInput
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      ĮPAKAVIMAS
                    </Typography>
                    <TextInput
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      PAKRAUTI
                    </Typography>
                    <TextInput
                      id="left"
                      name="left"
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '100px', display: 'inline-block', textAlign: 'left', mr: 1, ml: 1.25 }}
                    >
                      LIEKA
                    </Typography>
                    <TextInput
                      type="number"
                      size="small"
                      sx={{ ...borderInputSx, width: '77px', ml: 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.25, flexDirection: 'column' }}>
              <IconButton
                onClick={() => removeForm(index)}
                disabled={forms.length <= 1}
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.red',
                  fontSize: (theme: any) => theme.typography.fontSizes.heading2.xl,
                  p: 1.25,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, background-color 0.3s ease, color 0.3s ease',
                  border: 'none',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    color: 'text.red',
                    bgcolor: 'transparent',
                  },
                  '&:disabled': {
                    color: 'text.light',
                    transform: 'none',
                  },
                }}
              >
                <RemoveIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
              <IconButton
                id="add-loading-form"
                onClick={addForm}
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.green',
                  fontSize: (theme: any) => theme.typography.fontSizes.heading2.xl,
                  p: 1.25,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, background-color 0.3s ease, color 0.3s ease',
                  border: 'none',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    color: (theme: any) => theme.palette.text.green,
                    bgcolor: 'transparent',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface FormDeliveryProps {
  data?: {
    output?: {
      delivery_country?: string[];
      delivery_country_post_code?: string[];
      delivery_country_address?: string[];
      delivery_city?: string;
      delivery_place_from_date?: string[];
      delivery_place_to_date?: string[];
      delivery_reference_number?: string;
      delivery_company?: string;
    };
  };
}

type DeliveryFormType = {
  id?: number;
  delivery?: string;
  group2?: string;
  cargo?: string;
  loading?: string;
};

const FormDelivery = ({ data }: FormDeliveryProps) => {
  const [deliveryForms, setDeliveryForms] = useState<DeliveryFormType[]>([{}]);

  const addDeliveryForm = () => {
    const newDeliveryForm = { id: Date.now() };
    setDeliveryForms([...deliveryForms, newDeliveryForm]);
  };

  const removeDeliveryForm = (index: number) => {
    if (deliveryForms.length > 1) {
      const updatedForms = deliveryForms.filter((form, _i) => form.id !== deliveryForms[index].id);
      setDeliveryForms(updatedForms);
    }
  };


  const [formData, setFormData] = useState({
    delivery_country: data?.output?.delivery_country?.[0] || '',
    delivery_country_post_code: data?.output?.delivery_country_post_code?.[0] || '',
    delivery_country_address: data?.output?.delivery_country_address?.[0] || '',
    delivery_city: data?.output?.delivery_city || '',
    delivery_place_from_date: data?.output?.delivery_place_from_date || '',
    delivery_place_to_date: data?.output?.delivery_place_to_date || '',
    delivery_reference_number: data?.output?.delivery_reference_number || '',
    delivery_company: data?.output?.delivery_company || '',
  });
  useEffect(() => {
    const formatDate = (dateStr: string) => typeof dateStr === 'string' ? dateStr.slice(0, 16) : '';

    const rawFrom = data?.output?.delivery_place_from_date?.[0] || '';
    const rawTo = data?.output?.delivery_place_to_date?.[0] || '';

    setFormData({
      delivery_country: data?.output?.delivery_country?.[0] || '',
      delivery_country_post_code: data?.output?.delivery_country_post_code?.[0] || '',
      delivery_country_address: data?.output?.delivery_country_address?.[0] || '',
      delivery_place_from_date: formatDate(rawFrom),
      delivery_place_to_date: formatDate(rawTo),
      delivery_reference_number: data?.output?.delivery_reference_number || '',
      delivery_city: data?.output?.delivery_city || '',
      delivery_company: data?.output?.delivery_company || '',
    });
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name as string]: value as string }));
  };

  const inputSx = {
    borderRadius: 1,
    px: 0.25,
    py: 0.125,
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1px solid border.medium',
    },
  };

  const borderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.grey}`,
    },
  };

  const redBorderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.red}`,
    },
  };

  const greenBorderInputSx = {
    ...inputSx,
    borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
      borderLeft: (theme: any) => `4px solid ${theme.palette.text.green}`,
    },
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.green',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        p: 2.5,
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: 1,
      }}
    >
      <Box component="ul" id="delivery-forms-list" sx={{ width: '100%', flex: 1, listStyle: 'none', p: 0, m: 0 }}>
        {deliveryForms.map((form, index) => (
          <Box component="li" key={form.id} sx={{ display: 'flex' }}>
            <Box component="form" sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{
                        color: 'text.blue',
                        fontWeight: 700,
                        fontSize: (theme: any) => theme.typography.fontSizes.body,
                        width: '120px',
                        display: 'inline-block',
                        textAlign: 'right',
                        mr: 1,
                      }}
                    >
                      IŠKROVIMAS
                    </Typography>
                    <TextInput
                      id="delivery_company"
                      name="delivery_company"
                      value={formData.delivery_company}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, mr: 5.25, width: '180px' }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 3.625, width: '180px' }}
                    />
                    <TextInput
                      type="text"
                      id="delivery_country_address"
                      name="delivery_country_address"
                      value={formData.delivery_country_address}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, mr: 1, ml: 3.5, width: '315px' }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, width: '45px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ ml: 5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      FIX
                    </Typography>
                    <Checkbox />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      NUO
                    </Typography>
                    <TextInput
                      type="datetime-local"
                      id="from-date"
                      name="from-date"
                      value={formData.delivery_place_from_date}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      IKI
                    </Typography>
                    <TextInput
                      type="datetime-local"
                      id="to-date"
                      name="to-date"
                      value={formData.delivery_place_to_date}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, ml: 4.75, width: '180px' }}
                    />
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 4.125, width: '365px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      NR.
                    </Typography>
                    <TextInput
                      type="number"
                      id="delivery-start-date"
                      size="small"
                      sx={{ ...borderInputSx, width: '30px' }}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        component="label"
                        sx={{ ml: -1.125, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                      >
                        EIL. NR.
                      </Typography>
                      <TextInput
                        type="number"
                        id="delivery-start-date2"
                        size="small"
                        sx={{ ...borderInputSx, width: '30px' }}
                      />
                    </Box>
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small }}
                    >
                      TEL.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, ml: 4.375, width: '180px' }}
                    />
                    <TextInput
                      type="text"
                      id="delivery_country"
                      name="delivery_country"
                      value={formData.delivery_country}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...redBorderInputSx, mr: 0.625, ml: 3.5, width: '45px' }}
                    />
                    <TextInput
                      type="text"
                      id="delivery_country_post_code"
                      name="delivery_country_post_code"
                      value={formData.delivery_country_post_code}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...greenBorderInputSx, mr: 0.625, ml: 3.5, width: '65px' }}
                    />
                    <TextInput
                      type="text"
                      id="delivery_city"
                      name="delivery_city"
                      value={formData.delivery_city}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...inputSx, ml: 0.125, width: '215px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      SIUNTĖJAS
                    </Typography>
                    <TextInput
                      type="text"
                      id="sender"
                      name="sender"
                      size="small"
                      sx={{ ...borderInputSx, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      CMR NR.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '180px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 3.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      PLAT.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '95px' }}
                    />
                    <Typography
                      component="label"
                      sx={{ ml: 1.5, color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, mr: 1 }}
                    >
                      PLAT.
                    </Typography>
                    <TextInput
                      size="small"
                      sx={{ ...inputSx, mr: 0.625, width: '95px' }}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                      component="label"
                      sx={{ color: 'text.blue', fontSize: (theme: any) => theme.typography.fontSizes.small, width: '120px', display: 'inline-block', textAlign: 'right', mr: 1 }}
                    >
                      PAIEŠKA PLAT., ILG.
                    </Typography>
                    <TextInput
                      id="search"
                      name="search"
                      type="text"
                      size="small"
                      sx={{ ...borderInputSx, width: '180px' }}
                    />
                    <TextInput
                      type="text"
                      id="delivery_reference_number"
                      name="delivery_reference_number"
                      value={formData.delivery_reference_number}
                      onChange={handleChange}
                      size="small"
                      sx={{ ...borderInputSx, width: '365px', ml: 35.5 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.25, flexDirection: 'column' }}>
              <IconButton
                onClick={() => removeDeliveryForm(index)}
                disabled={deliveryForms.length <= 1}
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.red',
                  fontSize: (theme: any) => theme.typography.fontSizes.heading2.xl,
                  p: 1.25,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, background-color 0.3s ease, color 0.3s ease',
                  border: 'none',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    color: 'text.red',
                    bgcolor: 'transparent',
                  },
                  '&:disabled': {
                    color: 'text.light',
                    transform: 'none',
                  },
                }}
              >
                <RemoveIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
              <IconButton
                id="add-loading-form"
                onClick={addDeliveryForm}
                sx={{
                  bgcolor: 'transparent',
                  color: 'text.green',
                  fontSize: (theme: any) => theme.typography.fontSizes.heading2.xl,
                  p: 1.25,
                  cursor: 'pointer',
                  transition: 'transform 0.3s ease, background-color 0.3s ease, color 0.3s ease',
                  border: 'none',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    color: (theme: any) => theme.palette.text.green,
                    bgcolor: 'transparent',
                  },
                }}
              >
                <AddIcon sx={{ fontSize: 'inherit' }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

interface LoadingDeliveryFormProps {
  data?: {
    output?: {
      loading_country?: string[];
      loading_country_post_code?: string[];
      total_weight?: string;
      total_volume?: string;
      loading_reference_number?: string;
      loading_country_address?: string;
      loading_place_from_date?: string[];
      loading_place_to_date?: string[];
      loading_city?: string;
      temperature?: string;
      loading_company?: string;
      delivery_country?: string[];
      delivery_country_post_code?: string[];
      delivery_country_address?: string[];
      delivery_city?: string;
      delivery_place_from_date?: string[];
      delivery_place_to_date?: string[];
      delivery_reference_number?: string;
      delivery_company?: string;
    };
  };
}

const LoadingDeliveryForm = ({ data }: LoadingDeliveryFormProps) => (
  <Box
    sx={{
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      width: '91.666667%', // w-11/12
    }}
  >
    <FormLoading data={data} />
    <FormDelivery data={data} />
  </Box>
);

export default LoadingDeliveryForm;
